import { useNavigate } from "react-router-dom";
import { useLogin } from "../context/LoginContext";
import { useEffect, useState } from "react";
import { supabase } from "../client/client";
import PhoneIcon from "./PhoneIcon";
import UserIcon from "./UserIcon";
import LaptopIcon from "./LaptopIcon";
import MouseIcon from "./MouseIcon";
import PrinterIcon from "./PrinterIcon";
import KeyboardIcon from "./KeyboardIcon";
import Layout from "./Layout";
import Loading from "./Loading";

const StatCard = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center gap-4 hover:shadow-md transition-shadow">
    <div className={`p-3 rounded-full ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-3xl font-bold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
    </div>
  </div>
);

const DashboardContent = ({ error, stats }) => (
  <>
    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
        {error}
      </div>
    )}
    <div className="mb-8">
      <h1 className="text-[#4691A6] font-bold text-3xl">DASHBOARD</h1>
      <p className="text-gray-500 text-sm mt-1">Resumen del inventario — área ARBITRAJE</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      <StatCard
        title="Usuarios del área"
        value={stats.users}
        icon={<UserIcon className="w-7 h-7 text-blue-600" />}
        color="bg-blue-50"
      />
      <StatCard
        title="Total de equipos"
        value={stats.products}
        icon={<PhoneIcon className="w-7 h-7 text-teal-600" />}
        color="bg-teal-50"
      />
      <StatCard
        title="Laptops"
        value={stats.laptops}
        icon={<LaptopIcon className="w-7 h-7 text-indigo-500" />}
        color="bg-indigo-50"
      />
      <StatCard
        title="Mouses"
        value={stats.mouses}
        icon={<MouseIcon className="w-7 h-7 text-gray-600" />}
        color="bg-gray-100"
      />
      <StatCard
        title="Teclados"
        value={stats.keyboards}
        icon={<KeyboardIcon className="w-7 h-7 text-orange-500" />}
        color="bg-orange-50"
      />
      <StatCard
        title="Impresoras"
        value={stats.printers}
        icon={<PrinterIcon className="w-7 h-7 text-purple-500" />}
        color="bg-purple-50"
      />
    </div>
  </>
);

// standalone=true  → renders with Layout (direct /dashboard route)
// standalone=false → renders without Layout (embedded inside Home)
const Dashboard = ({ standalone = true }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const navigate              = useNavigate();
  const { getSessionAuth }    = useLogin();

  const [stats, setStats] = useState({
    users: 0, products: 0, laptops: 0, mouses: 0, keyboards: 0, printers: 0,
  });

  useEffect(() => {
    const sessionAuth = async () => {
      try {
        const session = await getSessionAuth();
        if (!session) { navigate("/", { replace: true }); return; }

        if (standalone) {
          // Only enforce admin check when accessed directly as a page
          const { data: userData, error: userError } = await supabase
            .from("users").select("role").eq("id", session.id).single();
          if (userError || userData?.role !== "admin") {
            navigate("/home", { replace: true });
            return;
          }
        }
        setLoading(false);
      } catch (e) {
        setError("Error al verificar sesión");
        setLoading(false);
      }
    };
    sessionAuth();
  }, []);

  const fetchData = async () => {
    try {
      const [users, products, laptops, mouses, keyboards, printers] = await Promise.all([
        supabase.from("area_users").select("id_user").eq("id_area", 25),
        supabase.from("equipos").select("id", { count: "exact", head: true }).ilike("area", "%ARBITRAJE%"),
        supabase.from("equipos").select("id", { count: "exact", head: true }).ilike("title", "%LAPTOP%").ilike("area", "%ARBITRAJE%"),
        supabase.from("equipos").select("id", { count: "exact", head: true }).ilike("title", "%MOUSE%").ilike("area", "%ARBITRAJE%"),
        supabase.from("equipos").select("id", { count: "exact", head: true }).ilike("title", "%TECLADO%").ilike("area", "%ARBITRAJE%"),
        supabase.from("equipos").select("id", { count: "exact", head: true }).ilike("title", "%IMPRESORA%").ilike("area", "%ARBITRAJE%"),
      ]);
      setStats({
        users:     !users.error     ? (users.data?.length || 0)  : 0,
        products:  !products.error  ? (products.count  || 0)     : 0,
        laptops:   !laptops.error   ? (laptops.count   || 0)     : 0,
        mouses:    !mouses.error    ? (mouses.count    || 0)     : 0,
        keyboards: !keyboards.error ? (keyboards.count || 0)     : 0,
        printers:  !printers.error  ? (printers.count  || 0)     : 0,
      });
    } catch (e) {
      console.error("Error fetching dashboard data:", e);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  if (standalone) {
    if (loading) return <Loading />;
    return (
      <Layout>
        <DashboardContent error={error} stats={stats} />
      </Layout>
    );
  }

  // Embedded inside another page — no Layout wrapper
  if (loading) return null;
  return <DashboardContent error={error} stats={stats} />;
};

export default Dashboard;
